import { useState, useCallback } from "react";
import { useCreate, useUpdate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  CheckCircle2, Loader2, FileText, Star, UserCheck,
  ArrowRight, PartyPopper,
} from "lucide-react";
import { ReviewRequestCard } from "./ReviewRequestCard";
import { SubCompletionCard } from "./SubCompletionCard";

interface JobCompletionPanelProps {
  jobId: string;
  contactId: string | number;
  contactName: string;
  jobStatus: string;
  estimatedTotal: number;
}

/**
 * Shown when job status is "completed" or "closed".
 * Guides the GC through the closing checklist:
 *   1. Send final invoice
 *   2. Rate subcontractors
 *   3. Request Google review
 *   4. Move customer to retention
 */
export function JobCompletionPanel({
  jobId,
  contactId,
  contactName,
  jobStatus,
  estimatedTotal,
}: JobCompletionPanelProps) {
  const [create] = useCreate();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [activatingRetention, setActivatingRetention] = useState(false);

  // Only show for completed / closed jobs
  if (jobStatus !== "completed" && jobStatus !== "closed") return null;

  const handleCreateFinalInvoice = useCallback(async () => {
    setSendingInvoice(true);
    try {
      await create("invoices", {
        data: {
          job_id: jobId,
          contact_id: contactId,
          invoice_type: "final",
          title: "Final Invoice",
          description: `Final invoice for project completion.`,
          subtotal: estimatedTotal,
          total: estimatedTotal,
        },
      }, { returnPromise: true });

      await create("events", {
        data: {
          contact_id: contactId,
          job_id: jobId,
          event_type: "invoice_created",
          payload: { invoice_type: "final", total: estimatedTotal },
        },
      }, { returnPromise: true });

      notify("Final invoice created", { type: "success" });
      refresh();
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setSendingInvoice(false);
    }
  }, [jobId, contactId, estimatedTotal, create, notify, refresh]);

  const handleActivateRetention = useCallback(async () => {
    setActivatingRetention(true);
    try {
      await update("contacts", {
        id: contactId,
        data: {
          lifecycle_stage: "retention",
          last_job_completed_at: new Date().toISOString(),
        },
      }, { returnPromise: true });

      await create("events", {
        data: {
          contact_id: contactId,
          job_id: jobId,
          event_type: "retention_activated",
          payload: { lifecycle_stage: "retention" },
        },
      }, { returnPromise: true });

      notify("Customer moved to retention lifecycle", { type: "success" });
      refresh();
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setActivatingRetention(false);
    }
  }, [jobId, contactId, update, create, notify, refresh]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-1">
        <PartyPopper className="w-4 h-4 text-green-600" />
        <h3 className="text-sm font-bold text-green-700">Job Completion Checklist</h3>
      </div>

      {/* Step 1: Final Invoice */}
      <Card className="border-green-200 bg-green-50/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold">1</div>
              <div>
                <p className="text-sm font-medium">Send Final Invoice</p>
                <p className="text-xs text-muted-foreground">
                  Create final invoice for ${estimatedTotal.toLocaleString()}
                </p>
              </div>
            </div>
            <button onClick={handleCreateFinalInvoice} disabled={sendingInvoice}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
              {sendingInvoice ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
              Create Invoice
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Rate Subs */}
      <SubCompletionCard jobId={jobId} contactId={contactId} />

      {/* Step 3: Review Request */}
      <ReviewRequestCard jobId={jobId} contactId={contactId} contactName={contactName} />

      {/* Step 4: Retention */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">4</div>
              <div>
                <p className="text-sm font-medium">Activate Retention</p>
                <p className="text-xs text-muted-foreground">
                  Move {contactName} to retention lifecycle for future work
                </p>
              </div>
            </div>
            <button onClick={handleActivateRetention} disabled={activatingRetention}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50">
              {activatingRetention ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
              Activate
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
